import formatRelative from 'date-fns/formatRelative';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import goldenRatio from '@glass/common/modules/goldenRatio/goldenRatio';
import {
  BASE_PATH_1_QUERY,
  BASE_PATH_2_QUERY,
  BASE_PATH_3_QUERY,
  RESUME_ID_QUERY,
} from '@glass/common/modules/pages/queryParams';
import { SPACE_STRING } from '@glass/common/modules/strings/constants';
import InlineCenterSkeleton from '@glass/web/components/Skeleton/InlineCenterSkeleton';
import CardHeader from '@glass/web/components/base/CardHeader';
import NoSsr from '@glass/web/components/base/NoSsr';
import Paper from '@glass/web/components/base/Paper';
import resumeSize from '@glass/web/modules/resumes/templates/resumeSize';
import { COLUMN, FLEX, SPACE_BETWEEN } from '@glass/web/modules/theme/flexConstants';
import makeStyles from '@glass/web/modules/theme/makeStyles';

import ResumeBuilderLink from '@glass/shared/components/Link/ResumeBuilderLink/ResumeBuilderLink';
import ResumeCardMenu from '@glass/shared/components/Profile/ResumeCardMenu';
import ResponsiveResumeImage from '@glass/shared/components/ResumeBuilder/ResponsiveResumeImage';
import createMemoPropKeys from '@glass/shared/modules/react/createMemoPropKeys';
import deleteResumeAction from '@glass/shared/modules/resumes/actions/deleteResumeAction';
import duplicateResumeAction from '@glass/shared/modules/resumes/actions/duplicateResumeAction';
import createSelectNextResumeBuilderParams from '@glass/shared/modules/resumes/selectors/createSelectNextResumeBuilderParams';

const IMAGE_WIDTH = 120;
const IMAGE_HEIGHT = (resumeSize.height * IMAGE_WIDTH) / resumeSize.width;

const sizes = `${IMAGE_WIDTH}px`;

const useStyles = makeStyles()((theme) => ({
  root: {
    transition: theme.transitions.create('opacity'),
    overflow: 'hidden',
  },
  container: {
    display: FLEX,
    position: 'relative',
  },
  deleting: {
    opacity: 0.3,
  },
  image: {
    width: IMAGE_WIDTH,
    height: IMAGE_HEIGHT,
    flexShrink: 0,
  },
  buttonAction: {
    marginLeft: theme.spacing(0.5),
    flexShrink: 0,
  },
  buttonActionPaddingText: {
    marginLeft: `${theme.spacing(0.25)}!important`,
  },
  headerContainer: {
    display: FLEX,
    justifyContent: SPACE_BETWEEN,
    flexDirection: COLUMN,
    overflow: 'hidden',
    width: '100%',
  },
}));

const areContinueParamsEqual = createMemoPropKeys([
  BASE_PATH_1_QUERY,
  BASE_PATH_2_QUERY,
  BASE_PATH_3_QUERY,
  RESUME_ID_QUERY,
]);

function ResumeCard({ resume, templateMeta, loading, prefetch }) {
  const { classes, cx } = useStyles();
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const dispatch = useDispatch();

  const selectContinueParams = useMemo(() => createSelectNextResumeBuilderParams(resume), [resume]);
  const editParams = useSelector(selectContinueParams, areContinueParamsEqual);

  const handleDuplicate = useCallback(
    (evt) => {
      if (duplicating) {
        evt.preventDefault();
        return null;
      }

      const handleEnd = () => setDuplicating(false);

      return dispatch(
        duplicateResumeAction({
          resumeId: resume?.id,
          onConfirm: () => setDuplicating(true),
          onUpdate: handleEnd,
        }),
      ).catch(handleEnd);
    },
    [dispatch, duplicating, resume?.id],
  );

  const handleDelete = useCallback(
    (evt) => {
      if (deleting) {
        evt.preventDefault();
        return null;
      }

      const handleEnd = () => setDeleting(false);

      return dispatch(
        deleteResumeAction({
          resumeId: resume?.id,
          onConfirm: () => setDeleting(true),
          onUpdate: handleEnd,
        }),
      ).catch(handleEnd);
    },
    [deleting, dispatch, resume?.id],
  );

  const subheader = useMemo(
    () =>
      resume?.updatedAt
        ? `Updated ${formatRelative(new Date(resume?.updatedAt), new Date())}`
        : null,
    [resume?.updatedAt],
  );

  const loadingSubheader = <InlineCenterSkeleton inline width={130} />;

  return (
    <Paper
      className={cx(classes.root, deleting && classes.deleting, classes.container)}
      variant="outlined"
    >
      {loading ? <InlineCenterSkeleton className={classes.image} variant="rectangular" /> : null}
      {!loading && templateMeta?.updatedAt ? (
        <ResponsiveResumeImage
          className={classes.image}
          contentResumeSlug="default"
          defaultWidth={IMAGE_WIDTH}
          sizes={sizes}
          template={templateMeta?.slug}
          updatedAt={templateMeta?.updatedAt}
        />
      ) : null}
      <div className={classes.headerContainer}>
        <CardHeader
          action={
            <ResumeCardMenu
              disabled={loading || deleting || duplicating}
              editParams={editParams}
              loading={loading || deleting || duplicating}
              resumeId={resume?.id}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
            />
          }
          subheader={
            loading ? loadingSubheader : <NoSsr fallback={loadingSubheader}>{subheader}</NoSsr>
          }
          title={
            loading ? (
              <>
                <InlineCenterSkeleton inline width={80} />
                {SPACE_STRING}
                <InlineCenterSkeleton inline width={80 * goldenRatio} />
              </>
            ) : (
              <ResumeBuilderLink params={editParams} prefetch={prefetch}>
                {resume?.name || `(No Name)`}
              </ResumeBuilderLink>
            )
          }
        />
      </div>
    </Paper>
  );
}

ResumeCard.propTypes = {
  loading: PropTypes.bool,
  prefetch: PropTypes.bool,
  resume: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    updatedAt: PropTypes.string,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  templateMeta: PropTypes.object,
};

ResumeCard.defaultProps = {
  loading: false,
};

export default React.memo(ResumeCard);
